import { get, isEmpty, reject } from 'lodash';

import {
  QUESTION_PROMPT_MAX_LENGTH,
  ANSWER_MAX_LENGTH,
} from 'containers/QuestionnairePage/constants';
import { getPromptFromHtml } from 'components/FormattedInput/helpers';
import { hasLogicVisibilityError } from 'components/VisibilityActionButton/utils';
import {
  hasMissingQuotaFromItems,
  isZeroQuotaAllowed,
  hasQuotaFilledFromItemsMatrix,
  hasQuotaLessThanCollectedFromItems,
  hasWrongQuotaSumFromItemsMatrix,
  hasWrongMinimumQuotaSumFromItemsMatrix,
} from 'utils/quotaHelpers';
import { deriveQuotaMode, QUOTA_MODE } from 'data/quotaModes';
import { surveyStatusEnum } from 'containers/SurveysPage/constants';

import { isCarriedForward, isMatrixAnswerEmpty } from './utils';
import {
  BIPOLAR_MIN_STATEMENTS,
  BIPOLAR_MAX_STATEMENTS,
  BIPOLAR_MIN_SCALEPOINTS,
  BIPOLAR_MAX_SCALEPOINTS,
  BIPOLAR_SLIDER_MIN_STATEMENTS,
  BIPOLAR_SLIDER_MAX_STATEMENTS,
  BIPOLAR_SLIDER_MIN_SCALEPOINTS,
  BIPOLAR_SLIDER_MAX_SCALEPOINTS,
  BIPOLAR_STYLES,
} from './constants';
import { hasIncompleteCarryForward } from '../../NodeSettings/settings/utils/CarryForward';

function getBipolarLimits(bipolarViewStyle) {
  if (bipolarViewStyle === BIPOLAR_STYLES.SLIDER) {
    return {
      minStatementsLimit: BIPOLAR_SLIDER_MIN_STATEMENTS,
      maxStatementsLimit: BIPOLAR_SLIDER_MAX_STATEMENTS,
      minScalepointsLimit: BIPOLAR_SLIDER_MIN_SCALEPOINTS,
      maxScalepointsLimit: BIPOLAR_SLIDER_MAX_SCALEPOINTS,
    };
  }
  return {
    minStatementsLimit: BIPOLAR_MIN_STATEMENTS,
    maxStatementsLimit: BIPOLAR_MAX_STATEMENTS,
    minScalepointsLimit: BIPOLAR_MIN_SCALEPOINTS,
    maxScalepointsLimit: BIPOLAR_MAX_SCALEPOINTS,
  };
}

function validate({ node, allNodes = [], answersWithLogicFromOutcomes }) {
  const { prompt, matrix, carryForward } = node;
  const errors = { answerTooLong: {} };
  const matrixStyle = get(
    matrix,
    'openEndedConfig.style',
    BIPOLAR_STYLES.HORIZONTAL,
  );
  const matrixAnswers = get(matrix, 'matrixAnswers', []);
  const matrixScalepoints = get(matrix, 'columns', []);
  const scalepointLabels = get(matrix, 'matrixExtraLabels', []);

  const {
    minStatementsLimit,
    maxStatementsLimit,
    minScalepointsLimit,
    maxScalepointsLimit,
  } = getBipolarLimits(matrixStyle);

  if (isEmpty(prompt)) {
    errors.missingPrompt = true;
  }

  const questionTooLong = prompt && prompt.length > QUESTION_PROMPT_MAX_LENGTH;
  if (questionTooLong) {
    errors.questionTooLong = true;
  }

  const hasInvalidAnswers = matrixAnswers.some(isMatrixAnswerEmpty);

  if (hasInvalidAnswers) {
    errors.emptyRows = true;
  }

  // answer prompt max length check

  matrixAnswers.forEach((matrixAnswer) => {
    if (
      matrixAnswer.beforeLabel?.length > ANSWER_MAX_LENGTH ||
      matrixAnswer.afterLabel?.length > ANSWER_MAX_LENGTH
    ) {
      errors.answerTooLong[matrixAnswer.answerRefId] = true;
    }
  });

  const nonEmptyAnswers = reject(matrixAnswers, isMatrixAnswerEmpty);
  if (
    nonEmptyAnswers.length < minStatementsLimit ||
    nonEmptyAnswers.length > maxStatementsLimit
  ) {
    errors.outOfLimitsRows = true;
  }

  if (
    matrixScalepoints.length < minScalepointsLimit ||
    matrixScalepoints.length > maxScalepointsLimit
  ) {
    errors.outOfLimitsColumns = true;
  }

  if (isEmpty(errors.answerTooLong)) {
    delete errors.answerTooLong;
  }

  if (
    scalepointLabels.length > 0 &&
    scalepointLabels.some(({ label }) => isEmpty(getPromptFromHtml(label)))
  ) {
    errors.emptyScalepointLabels = true;
  }

  const carryForwardEnabled =
    matrixAnswers.some(isCarriedForward) ||
    matrixScalepoints.some(isCarriedForward);

  if (carryForwardEnabled) {
    const hasIncompleteCarryForwardConfigurations = hasIncompleteCarryForward(
      carryForward,
      allNodes,
    );
    if (hasIncompleteCarryForwardConfigurations) {
      errors.incompleteCarryForwardConfiguration = true;
    }
  }

  if (
    hasLogicVisibilityError({
      nodeType: node.type,
      isScreening: node.isScreening,
      answers: nonEmptyAnswers,
      answersWithLogicFromOutcomes,
    })
  ) {
    errors.hasLogicVisibilityErrorOnRows = true;
  }

  return errors;
}

export default validate;

export const validateQuotas = (
  node,
  nodeQuotas,
  uncoditionallyHiddenItems,
  surveyConfig,
  surveyStatusName,
) => {
  const { isDraft, completes } = surveyConfig;
  const errors = {};
  const isMinimumResponses =
    deriveQuotaMode({
      simpleQuotasPresent: true,
      quotaLimitType: node.quotaLimitType,
    }) === QUOTA_MODE.MIN;

  const isSurveyCompleted = surveyStatusName === surveyStatusEnum.COMPLETED;

  const answers = get(node, 'answers', []);
  const columns = get(node, 'matrix.columns', []);
  const visibleAnswers = answers.filter(
    (answer) => !uncoditionallyHiddenItems.includes(answer.refId),
  );
  const visibleColumns = columns.filter(
    (scorepoint) => !uncoditionallyHiddenItems.includes(scorepoint.refId),
  );

  const visibleItemKeys = visibleAnswers.reduce((keys, answer) => {
    visibleColumns.forEach((column) => {
      keys.push(`${answer.refId}::${column.refId}`);
    });
    return keys;
  }, []);

  if (
    !isMinimumResponses &&
    !isSurveyCompleted &&
    hasQuotaFilledFromItemsMatrix(
      nodeQuotas.items,
      visibleAnswers,
      visibleColumns,
    )
  ) {
    errors.quotasFulfilled = true;
  }

  if (
    hasMissingQuotaFromItems(visibleItemKeys, nodeQuotas.items, {
      allowZeroQuota: isZeroQuotaAllowed({
        isDraft,
        isScreening: node.isScreening,
      }),
      isMinimumResponses,
    })
  ) {
    if (isMinimumResponses) {
      errors.minimumResponsesMissing = true;
    } else {
      errors.quotaMissing = true;
    }
    return errors;
  }

  if (!errors.quotasFulfilled && !isSurveyCompleted) {
    if (isMinimumResponses) {
      if (
        hasWrongMinimumQuotaSumFromItemsMatrix({
          nodeQuotaItems: nodeQuotas.items,
          remainingCompletes: completes,
          rows: visibleAnswers,
          columns: visibleColumns,
        })
      ) {
        errors.minimumQuotaSumExceedsCompletes = true;
        return errors;
      }
    } else if (
      hasWrongQuotaSumFromItemsMatrix({
        nodeQuotaItems: nodeQuotas.items,
        remainingCompletes: completes,
        rows: visibleAnswers,
        columns: visibleColumns,
      })
    ) {
      errors.quotaSumIsNot100 = true;
      return errors;
    }
  }

  if (
    !isMinimumResponses &&
    hasQuotaLessThanCollectedFromItems(visibleItemKeys, nodeQuotas.items)
  ) {
    errors.hasQuotaLessThanCollected = true;
    return errors;
  }

  return errors;
};
